import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Form from '@atlaskit/form';
import { B300, N200, N500, N600 } from '@atlaskit/theme/colors';
import {
  SanitizedFormattedHtmlMessage,
  useStargateClient
} from '@atlassiansox/bxpkit-core';

import styled from '@emotion/styled';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useDevopsState } from '../../hook';
import { messages } from '../../messages';
import {
  useAppDispatch,
  useAppSelector,
  updateSharedFormDataResendEmailAddress
} from '../../../../redux';
import {
  ButtonGoogleSignIn,
  ButtonSubmit,
  InputEmail,
  InputName
} from '../component';

import { MarketingConsentCheckBox } from '../component';
import { ResendEmailDevopsPage } from '../slide/slide-n2n-resend-email';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
  fontCharlieDisplay,
  fontCharlieText,
  SubmitErrorMessage
} from '../styled';
import { FormattedMessage } from 'react-intl';

type FormProps = {
  readonly formProps: {
    readonly onSubmit: (e: React.FormEvent) => void;
    readonly ref: React.RefObject<HTMLFormElement>;
    readonly onKeyDown: (e: React.KeyboardEvent) => void;
  };
  readonly submitting: boolean;
  readonly change: any;
  readonly getValues: any;
  readonly getState: any;
};

function shouldSkipRecaptcha(email: string) {
  const botTestPattern =
    /(bottest@usersinbuckets.com|bottest@atl-paid-dev.com|bottest@bxpwpi.com)$/;
  return botTestPattern.test(email);
}

export function DevopsFormLoggedOutDeferredPassword({
  setGRecaptchaResponse
}: {
  setGRecaptchaResponse: React.Dispatch<React.SetStateAction<string>>;
}) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const dispatch = useAppDispatch();
  const devopsState = useDevopsState();
  const { enableSkipRecaptcha, locale, intl } = devopsState;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInputs, setShowInputs] = useState(false);
  const stargateClient = useStargateClient();
  const showMarketinConsent = locale.localeRequiresMarketingCommunicationOptIn;
  const recaptchaRef = useRef<any | null>();
  const navigate = useNavigate();
  const { createAnalyticsEvent } = useAnalyticsEvents();

  const { email: emailValidation, fullName: fullNameValidation } =
    useAppSelector(
      ({ n2nDeferredPasswordFieldValidation }) =>
        n2nDeferredPasswordFieldValidation
    );

  const submitErrorMessage = useAppSelector(
    ({ sharedFormData: { submitErrorMessage } }) => submitErrorMessage
  );

  useEffect(() => {
    navigate('/sign-up-form');
    recaptchaRef?.current?.reset();
  }, []);

  const useEmailButton = () => {
    setShowInputs(true);
  };

  const onSubmitWithReCAPTCHA = useCallback(
    async (data: any) => {
      setIsSubmitting(true);
      if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
      }
      const { email, fullName } = data;
      dispatch(updateSharedFormDataResendEmailAddress(email));
      const skipRecaptcha =
        data.email && enableSkipRecaptcha && shouldSkipRecaptcha(data.email);

      const token = skipRecaptcha
        ? 'reCaptcha'
        : await executeRecaptcha('userSignup');

      if (token != null) {
        const gRecaptchaResponse = token;
        setGRecaptchaResponse(gRecaptchaResponse);

        createAnalyticsEvent({
          eventName: 'bxp.formCloudSignup.submitWithCaptcha',
          eventComponent: 'button',
          event: 'clicked'
        }).fire();
        createAnalyticsEvent({
          eventName: 'bxp.formCloudSignup.formSubmit',
          eventComponent: 'button',
          event: 'clicked',
          data: {
            type: 'N2N'
          }
        }).fire();

        await stargateClient
          .userSignup({
            email: email as string,
            locale: intl?.locale || 'en',
            redirectTo: window.location.href,
            displayName: fullName,
            gRecaptchaResponse
          })
          .then(({ otpRedirect }) => {
            window.location.assign(otpRedirect || '');
          })
          .catch((error) => {
            createAnalyticsEvent({
              eventName: 'reactSignUpFormSubmitFailed',
              eventComponent: 'button',
              event: 'clicked',
              data: {
                event: 'submitFailed',
                uuid: 'reactSignUpForm'
              }
            }).fire();
            console.log(error);
            setIsSubmitting(false);
          });

        createAnalyticsEvent({
          eventName: 'reactSignUpFormSubmitSucceeded',
          eventComponent: 'button',
          event: 'clicked',
          data: {
            event: 'submitSucceeded',
            uuid: 'reactSignUpForm'
          }
        }).fire();

        navigate('/resend-email');
      } else {
        setIsSubmitting(false);
        throw new Error('Invalid Recaptcha Token');
      }
      return token;
    },
    [executeRecaptcha]
  );

  return (
    <>
      <Routes>
        <Route
          path="/sign-up-form"
          element={
            <>
              <Header>
                <SanitizedFormattedHtmlMessage
                  {...messages.welcomeMessageLoggedOut}
                />
              </Header>
              <Form onSubmit={onSubmitWithReCAPTCHA} isDisabled={isSubmitting}>
                {({ formProps }: FormProps) => (
                  <StyledForm {...formProps}>
                    <>
                      <ButtonGoogleSignIn />
                      <Divider>
                        <LeftLine />
                        <FormattedMessage {...messages.socialSignUpOr} />
                        <RightLine />
                      </Divider>
                      <SocialButton
                        onClick={useEmailButton}
                        showInput={showInputs}
                      >
                        <FormattedMessage
                          {...messages.socialSignUpButtonSignUpWithEmail}
                        />
                      </SocialButton>
                    </>

                    <InputsContainer showInput={showInputs}>
                      <InputEmail />
                      <InputName />
                      {showMarketinConsent && <MarketingConsentCheckBox />}
                      <ButtonSubmit
                        isLoading={isSubmitting}
                        isDisabled={
                          !(
                            emailValidation === 'success' &&
                            fullNameValidation === 'success'
                          )
                        }
                      />
                    </InputsContainer>
                  </StyledForm>
                )}
              </Form>
              {submitErrorMessage && (
                <SubmitErrorMessage>
                  <SanitizedFormattedHtmlMessage {...submitErrorMessage} />
                </SubmitErrorMessage>
              )}
              {/* TODO: EULA onclick analytics */}
              <Eula
                onClick={() => {
                  createAnalyticsEvent({
                    eventName: 'bxp.formCloudSignup.eulaClicked',
                    eventComponent: 'button',
                    event: 'clicked'
                  }).fire();
                }}
                showInput={showInputs}
              >
                <SanitizedFormattedHtmlMessage {...messages.devopsEula} />
              </Eula>
              <NoCard>
                <SanitizedFormattedHtmlMessage {...messages.noCard} />
              </NoCard>
            </>
          }
        />
        <Route path="/resend-email" element={<ResendEmailDevopsPage />} />
      </Routes>
    </>
  );
}
type ShowInputsProps = {
  readonly showInput: boolean;
};
const SocialButton = styled.div<ShowInputsProps>`
  display: ${(props) => (props.showInput ? 'none' : 'flex')};
  height: 39px;
  width: 100%;
  border-radius: 3px;
  justify-content: center;
  align-items: center;
  color: ${N500};
  cursor: pointer;
  font-weight: 500;
  box-shadow: rgba(0, 0, 0, 0.2) 1px 1px 5px 0 !important;

  margin: 0px 0px 20px 0px;
  padding: 0;
  border: 0;
  font-size: 100%;
  vertical-align: baseline;
  font-family: Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
    Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif;
  font-style: normal;
  background-color: #e8e9ee;
`;
const Divider = styled.div`
  font-family: Charlie Text, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
    Noto Sans, Ubuntu, Droid Sans, Helvetica Neue, sans-serif;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #42526e;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
`;
const LeftLine = styled.div`
  border-bottom: 1px solid #d8d8d8;
  width: 31px;
  margin-right: 8px;
`;
const RightLine = styled.div`
  border-bottom: 1px solid #d8d8d8;
  width: 31px;
  margin-left: 8px;
`;

const InputsContainer = styled.div<ShowInputsProps>`
  display: ${(props) => (props.showInput ? 'block' : 'none')};
`;

const Header = styled.h1`
  font-family: ${fontCharlieDisplay};
  font-style: normal;
  font-weight: 500;
  -webkit-font-smoothing: antialiased;
  font-size: 32px;
  line-height: 30px;
  color: ${N600};
  letter-spacing: 0.3px;
  max-width: 300px;
  margin: 0 auto 30px auto;
  text-align: center;

  @media only screen and (max-width: 769px) {
    font-size: 24px;
    line-height: 20px;
    text-align: center;
  }
`;

const Eula = styled.div<ShowInputsProps>`
  font-family: ${fontCharlieText};
  font-size: 12px;
  line-height: 16px;
  color: ${N200};
  vertical-align: middle;
  padding: 5px 0;
  font-weight: 400;
  display: ${(props) => (props.showInput ? 'block' : 'none')};

  a {
    color: ${B300};
    text-decoration: none;
    cursor: pointer;
  }

  p {
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 24px;
    margin-top: 12px;
  }
`;

const NoCard = styled.div`
  font-family: ${fontCharlieDisplay};
  font-weight: 500;
  font-size: 0.875rem;
  letter-spacing: 1px;
  line-height: 1.25;
  font-style: normal;
  margin-top: 15px;
  margin-bottom: 0;
  text-align: center;
  text-transform: uppercase;
`;

const StyledForm = styled.form`
  font-family: ${fontCharlieText};

  label {
    color: ${N200};
    font-weight: 500;

    span {
      display: none;
    }
  }
`;
